import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowLeft, ChefHat, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/seo";

export default function NotFoundScreen() {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 px-4 py-20">
      <SEO 
        title="Page Not Found"
        description="The page you're looking for doesn't exist. Let's get you back to our delicious menu."
        noindex={true}
      />
      <div className="max-w-2xl w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-6"
          >
            <h1 className="text-9xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              404
            </h1>
          </motion.div>

          {/* Error Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Oops! Page Not Found
            </h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              The page you're looking for seems to have wandered off. 
              Don't worry, let's get you back on track!
            </p>
          </motion.div>

          {/* Illustration Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mb-8"
          >
            <Card className="bg-card/50 backdrop-blur-xl border-border/50 shadow-xl">
              <CardContent className="p-8">
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="flex justify-center mb-4"
                >
                  <div className="relative">
                    <ChefHat className="h-24 w-24 text-primary/60" />
                    <motion.div
                      animate={{
                        rotate: [0, 10, -10, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="absolute -top-2 -right-2"
                    >
                      <Search className="h-8 w-8 text-muted-foreground" />
                    </motion.div>
                  </div>
                </motion.div>
                <p className="text-sm text-muted-foreground">
                  Even our chef couldn't find this page!
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              onClick={() => navigate("/")}
              size="lg"
              className="min-w-[160px] shadow-lg hover:shadow-xl transition-shadow"
            >
              <Home className="mr-2 h-5 w-5" />
              Go Home
            </Button>
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="lg"
              className="min-w-[160px]"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Go Back
            </Button>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-12"
          >
            <p className="text-sm text-muted-foreground mb-4">
              Or try one of these popular pages:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate("/our-menu")}
                className="rounded-xl"
              >
                Our Menu
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/shop")}
                className="rounded-xl"
              >
                Shop
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/services")}
                className="rounded-xl"
              >
                Services
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/contact")}
                className="rounded-xl"
              >
                Contact
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

